import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StandaloneJanuaryReport from './StandaloneJanuaryReport';
import StandaloneFebruaryReport from './StandaloneFebruaryReport';
import StandaloneMarchReport from './StandaloneMarchReport';
import StandaloneAprilReport from './StandaloneAprilReport';
import StandaloneMayReport from './StandaloneMayReport';
import StandaloneQ1Report from './StandaloneQ1Report';
import { InlineDemoNotice } from '../common/DemoModeIndicator';

/**
 * Standalone static reports component with integrated navigation
 * Renders the report components directly without any API calls
 */
const StaticReports: React.FC = () => {
  const [currentPeriod, setCurrentPeriod] = useState<'january' | 'february' | 'march' | 'april' | 'may' | 'quarterly'>('may');

  const handlePeriodChange = (period: 'january' | 'february' | 'march' | 'april' | 'may' | 'quarterly') => {
    setCurrentPeriod(period);
  };

  // CSS classes for tab styling
  const tabClass = "px-4 py-2 text-center font-medium cursor-pointer";
  const activeTabClass = "border-b-2 border-blue-500 text-blue-600";
  const inactiveTabClass = "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent";

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Link 
            to="/reports"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
          >
            ← Back to Reports
          </Link>
        </div>
        <div className="text-xl font-bold text-gray-800">
          {currentPeriod === 'january' ? 'January 2025 Report' : 
           currentPeriod === 'february' ? 'February 2025 Report' : 
           currentPeriod === 'march' ? 'March 2025 Report' : 
           currentPeriod === 'april' ? 'April 2025 Report' : 
           currentPeriod === 'may' ? 'May 2025 Report' : 
           'Q1 2025 Report'}
        </div>
        <div className="invisible">
          {/* Placeholder for flex spacing */}
          <button className="px-4 py-2 bg-transparent"></button>
        </div>
      </div>

      <InlineDemoNotice>
        <p>
          These are static demonstration reports using sample data from Q1 2025. 
          To view real reports from your uploaded data, please run the application 
          with the Laravel backend or upload new CSV files.
        </p>
      </InlineDemoNotice>

      {/* Navigation Tabs */}
      <div className="border border-gray-200 rounded-t-lg bg-white mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-1" aria-label="Report periods">
            <button
              onClick={() => handlePeriodChange('january')}
              className={`${tabClass} ${currentPeriod === 'january' ? activeTabClass : inactiveTabClass}`}
              aria-current={currentPeriod === 'january' ? 'page' : undefined}
            >
              January 2025
            </button>
            <button
              onClick={() => handlePeriodChange('february')}
              className={`${tabClass} ${currentPeriod === 'february' ? activeTabClass : inactiveTabClass}`}
              aria-current={currentPeriod === 'february' ? 'page' : undefined}
            >
              February 2025
            </button>
            <button
              onClick={() => handlePeriodChange('march')}
              className={`${tabClass} ${currentPeriod === 'march' ? activeTabClass : inactiveTabClass}`}
              aria-current={currentPeriod === 'march' ? 'page' : undefined}
            >
              March 2025
            </button>
            <button
              onClick={() => handlePeriodChange('april')}
              className={`${tabClass} ${currentPeriod === 'april' ? activeTabClass : inactiveTabClass}`}
              aria-current={currentPeriod === 'april' ? 'page' : undefined}
            >
              April 2025
            </button>
            <button
              onClick={() => handlePeriodChange('may')}
              className={`${tabClass} ${currentPeriod === 'may' ? activeTabClass : inactiveTabClass}`}
              aria-current={currentPeriod === 'may' ? 'page' : undefined}
            >
              May 2025
            </button>
            <button
              onClick={() => handlePeriodChange('quarterly')}
              className={`${tabClass} ${currentPeriod === 'quarterly' ? activeTabClass : inactiveTabClass}`}
              aria-current={currentPeriod === 'quarterly' ? 'page' : undefined}
            >
              Q1 2025
            </button>
          </nav>
        </div>
        
        <div className="p-3 text-sm text-gray-600">
          <p>Static sample report data for demonstration purposes.</p>
        </div>
      </div>

      {/* Report Content */}
      <div className="report-container">
        {currentPeriod === 'january' ? (
          <StandaloneJanuaryReport />
        ) : currentPeriod === 'february' ? (
          <StandaloneFebruaryReport />
        ) : currentPeriod === 'march' ? (
          <StandaloneMarchReport />
        ) : currentPeriod === 'april' ? (
          <StandaloneAprilReport />
        ) : currentPeriod === 'may' ? (
          <StandaloneMayReport />
        ) : (
          <StandaloneQ1Report />
        )}
      </div>
    </div>
  );
};

export default StaticReports;